import { Routes, Route } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Users,
  Plus,
  Search,
  Edit,
  FileSpreadsheet,
  FileText,
  Trash2,
  Eye,
  Printer,
  X,
  Clipboard,
  CheckCircle2,
} from "lucide-react";
import { studentService, type Student, type Parent } from "../services/studentService";
import { usePermissions } from "../hooks/usePermissions";
import StudentProfileModal from "../components/students/StudentProfileModal";
import StudentEditModal from "../components/students/StudentEditModal";
import DeleteStudentDialog from "../components/students/DeleteStudentDialog";
import {
  primaryGuardian,
  STUDENT_STATUS_DOTS,
  studentInitials,
} from "../components/students/studentDisplay";
import { copyText } from "../utils/clipboard";
import {
  DEFAULT_SCHOOL_EMAIL_DOMAIN,
  schoolEmail,
} from "../utils/schoolEmail";
import {
  useUsernameSuggestion,
  usernameHint,
} from "../hooks/useUsernameSuggestion";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../utils/exportTable";
import {
  activeStudentFilterCount,
  EMPTY_STUDENT_FILTERS,
  STUDENT_DIRECTORY_PAGE_SIZES,
  STUDENT_ORDERINGS,
  STUDENT_STATUS_TABS,
  studentDirectoryParams,
  type StudentDirectoryFilters,
} from "./studentDirectoryFilters";
// Form validation schema
const studentSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z
    .string()
    .min(8, "Temporary password must be at least 8 characters"),
  // No email: every account is reached at its username on the school domain,
  // so the address is worked out rather than entered.
  first_name: z.string().min(2, "First name is required"),
  last_name: z.string().min(2, "Last name is required"),
  phone_number: z.string().optional(),
  // student_id and admission_number are auto-generated
  date_of_birth: z.string().min(1, "Date of birth is required"),
  gender: z.enum(["male", "female"], {
    required_error: "Please select gender",
  }),
  blood_group: z.string().optional(),
  nationality: z.string().min(2, "Nationality is required"),
  admission_date: z.string().min(1, "Admission date is required"),
  current_class: z.number().positive("Please select a class"),
  medical_conditions: z.string().optional(),
  allergies: z.string().optional(),
  medications: z.string().optional(),
  doctor_name: z.string().optional(),
  doctor_phone: z.string().optional(),
  uses_transport: z.boolean().default(false),
  residence: z.enum(["day", "boarding"], { errorMap: () => ({ message: "Choose day or boarding" }) }),
  transport_route: z.string().optional(),
  pickup_location: z.string().optional(),
  surepay_number: z.string().optional(),
  previous_school: z.string().optional(),
  notes: z.string().optional(),
  parent_username: z.string().optional(),
  parent_password: z.string().optional(),
  parent_first_name: z.string().optional(),
  parent_last_name: z.string().optional(),
  parent_phone_number: z.string().optional(),
  parent_relationship: z.string().optional(),
  // Set when the admitter confirms a namesake born the same day is another child.
  confirm_not_duplicate: z.boolean().optional(),
});

type StudentFormData = z.infer<typeof studentSchema>;

function generatePassword() {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  const values = crypto.getRandomValues(new Uint32Array(14));
  return `S!${Array.from(values, (value) => alphabet[value % alphabet.length]).join("")}`;
}

function submissionError(error: unknown) {
  const data = (
    error as { response?: { data?: Record<string, string[] | string> } }
  )?.response?.data;
  if (!data)
    return "The student could not be admitted. Please check the information and try again.";
  return Object.entries(data)
    .map(
      ([field, message]) =>
        `${field.replaceAll("_", " ")}: ${Array.isArray(message) ? message.join(" ") : message}`,
    )
    .join(" ");
}

/** The server's "already on the register" answer, which the admitter may overrule. */
function possibleDuplicate(error: unknown) {
  return Boolean(
    (error as { response?: { data?: Record<string, unknown> } })?.response?.data?.possible_duplicate,
  );
}

function StudentsList() {
  // What is typed in the search box reaches the filters after a pause, so the
  // directory is not re-fetched on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(EMPTY_STUDENT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const directoryParams = useMemo(() => studentDirectoryParams(filters), [filters]);
  const activeFilterCount = activeStudentFilterCount(filters);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addParentDuringAdmission, setAddParentDuringAdmission] =
    useState(false);
  const [parentLinkMode, setParentLinkMode] = useState<"new" | "existing">("new");
  const [existingParentSearch, setExistingParentSearch] = useState("");
  const [existingParentSearchDebounced, setExistingParentSearchDebounced] = useState("");
  const [selectedExistingParent, setSelectedExistingParent] = useState<Parent | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<{
    name: string;
    username: string;
    password: string;
    parentUsername?: string;
    parentPassword?: string;
  } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [viewStudent, setViewStudent] = useState<Student | null>(null);
  const [editStudent, setEditStudent] = useState<Student | null>(null);
  const [returnToProfile, setReturnToProfile] = useState(false);
  const [studentPendingDeletion, setStudentPendingDeletion] =
    useState<Student | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const queryClient = useQueryClient();
  const { hasCapability } = usePermissions();
  const canViewFinancials = hasCapability("financial.view_financial_info");
  const canManageStudents = hasCapability("access.students.student_write");

  const {
    data: studentsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["students", directoryParams, currentPage, pageSize],
    queryFn: () =>
      studentService.getStudentsPage({
        ...directoryParams,
        page: currentPage,
        page_size: pageSize,
      }),
    keepPreviousData: true,
  });

  const students = studentsData?.results || [];
  const totalCount = studentsData?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstVisible = totalCount ? (currentPage - 1) * pageSize + 1 : 0;
  const lastVisible = Math.min(currentPage * pageSize, totalCount);
  const classesQuery = useQuery({
    queryKey: ["classes", "student-admission"],
    queryFn: () => studentService.getClasses({ ordering: "level" }),
  });

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setExistingParentSearchDebounced(existingParentSearch.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [existingParentSearch]);
  const existingParentsQuery = useQuery({
    queryKey: ["existing-parent-search", existingParentSearchDebounced],
    queryFn: () => studentService.searchParents(existingParentSearchDebounced),
    enabled: existingParentSearchDebounced.length >= 2,
  });

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<StudentFormData>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      uses_transport: false,
      password: generatePassword(),
    },
  });

  const usesTransport = watch("uses_transport");
  const studentEmail = schoolEmail(watch("username") || "");
  const parentEmail = schoolEmail(watch("parent_username") || "");

  // The learner and the parent are each named the way everyone on the system
  // is named -- first initial + last name -- unless the admitter types their
  // own. The server settles which of two same-named people gets the counter.
  const [studentUsernameEdited, setStudentUsernameEdited] = useState(false);
  const [parentUsernameEdited, setParentUsernameEdited] = useState(false);
  const studentUsername = useUsernameSuggestion(
    watch("first_name") || "",
    watch("last_name") || "",
  );
  const parentUsername = useUsernameSuggestion(
    watch("parent_first_name") || "",
    watch("parent_last_name") || "",
  );

  const fill = (
    field: "username" | "parent_username",
    value: string,
    edited: boolean,
  ) => {
    if (edited) return;
    setValue(field, value, { shouldValidate: value.length >= 3 });
  };
  useEffect(() => {
    fill("username", studentUsername.base, studentUsernameEdited);
  }, [studentUsername.base, studentUsernameEdited]);
  useEffect(() => {
    if (!studentUsername.suggested) return;
    fill("username", studentUsername.suggested, studentUsernameEdited);
  }, [studentUsername.suggested, studentUsernameEdited]);
  useEffect(() => {
    fill("parent_username", parentUsername.base, parentUsernameEdited);
  }, [parentUsername.base, parentUsernameEdited]);
  useEffect(() => {
    if (!parentUsername.suggested) return;
    fill("parent_username", parentUsername.suggested, parentUsernameEdited);
  }, [parentUsername.suggested, parentUsernameEdited]);
  // IDs are numbered within the year: STU260001 is the first learner of 2026.
  const yearDigits = String(new Date().getFullYear()).slice(-2);

  // Create student mutation
  const createStudentMutation = useMutation({
    mutationFn: studentService.createStudent,
    onSuccess: (_student, variables) => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      setShowAddModal(false);
      setCopyFeedback("");
      setCreatedCredentials({
        name: `${variables.first_name} ${variables.last_name}`,
        username: variables.username,
        password: variables.password,
        parentUsername: variables.parent_username,
        parentPassword: variables.parent_password,
      });
      reset({ uses_transport: false, password: generatePassword() });
      // The next learner is named from their own name again.
      setStudentUsernameEdited(false);
      setParentUsernameEdited(false);
      setAddParentDuringAdmission(false);
      setParentLinkMode("new");
      setExistingParentSearch("");
      setSelectedExistingParent(null);
    },
  });
  const deleteStudentMutation = useMutation({
    mutationFn: studentService.deleteStudent,
    onSuccess: () => {
      setStudentPendingDeletion(null);
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const onSubmit = (data: StudentFormData) => {
    if (addParentDuringAdmission && parentLinkMode === "existing") {
      const payload: Record<string, unknown> = { ...data };
      delete payload.parent_username;
      delete payload.parent_password;
      delete payload.parent_first_name;
      delete payload.parent_last_name;
      delete payload.parent_phone_number;
      delete payload.parent_relationship;
      payload.existing_parent_id = selectedExistingParent?.id;
      createStudentMutation.mutate(payload);
      return;
    }
    createStudentMutation.mutate(data);
  };

  const updateFilters = (changes: Partial<StudentDirectoryFilters>) => {
    setFilters((current) => ({ ...current, ...changes }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setFilters(EMPTY_STUDENT_FILTERS);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (searchInput === filters.search) return;
    const timeout = window.setTimeout(
      () => updateFilters({ search: searchInput }),
      300,
    );
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const exportStudents = async (format: "csv" | "excel" | "pdf") => {
    setIsExporting(true);
    try {
      const allStudents: Student[] = [];
      let page = 1;
      let response: Awaited<ReturnType<typeof studentService.getStudents>>;
      do {
        response = await studentService.getStudents({
          ...directoryParams,
          page,
          page_size: 100,
        });
        allStudents.push(...response.results);
        page += 1;
      } while (response.next);

      const columns = [
        { header: "Student", accessor: (student: Student) => student.full_name },
        { header: "Student ID", accessor: (student: Student) => student.student_id },
        { header: "Admission number", accessor: (student: Student) => student.admission_number },
        { header: "Class", accessor: (student: Student) => student.current_class_name || "Unassigned" },
        { header: "Gender", accessor: (student: Student) => student.gender },
        { header: "Age", accessor: (student: Student) => student.age },
        { header: "Residence", accessor: (student: Student) => student.residence || "" },
        { header: "Transport", accessor: (student: Student) => (student.uses_transport ? "Yes" : "No") },
        { header: "Parent / guardian", accessor: (student: Student) => primaryGuardian(student)?.name || "" },
        { header: "Parent phone", accessor: (student: Student) => primaryGuardian(student)?.phone || "" },
        { header: "Status", accessor: (student: Student) => student.status },
        { header: "Email", accessor: (student: Student) => student.user.email || "" },
      ];
      const filename = `student-directory-${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") exportAsCsv(`${filename}.csv`, columns, allStudents);
      else if (format === "excel") exportAsExcel(`${filename}.xls`, "Students", columns, allStudents);
      else exportAsPdf("Student directory", columns, allStudents);
    } finally {
      setIsExporting(false);
    }
  };


  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">Error loading students</div>
          <button onClick={() => refetch()} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Students</h1>
          <p className="text-gray-600">
            Manage admissions, learner records, placement, and portal access.
          </p>
        </div>
        {canManageStudents && (
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Student
          </button>
        )}
      </div>

      {createdCredentials && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
            <div className="flex-1">
              <h2 className="font-semibold text-green-900">
                {createdCredentials.name} was admitted successfully
              </h2>
              <p className="mt-1 text-sm text-green-800">
                Copy the student portal credentials now.
              </p>
              <div className="mt-3 rounded bg-white p-3 font-mono text-sm">
                Username: {createdCredentials.username}
                <br />
                Temporary password: {createdCredentials.password}
                {createdCredentials.parentUsername && (
                  <>
                    <br />
                    <br />
                    Parent username: {createdCredentials.parentUsername}
                    <br />
                    Parent temporary password:{" "}
                    {createdCredentials.parentPassword}
                  </>
                )}
              </div>
              <button
                className="btn btn-outline mt-3 px-3 py-2"
                onClick={async () => {
                  const copied = await copyText(
                    `Portal: ${location.origin}\nStudent username: ${createdCredentials.username}\nStudent temporary password: ${createdCredentials.password}${createdCredentials.parentUsername ? `\n\nParent username: ${createdCredentials.parentUsername}\nParent temporary password: ${createdCredentials.parentPassword}` : ""}`,
                  );
                  setCopyFeedback(
                    copied
                      ? "Credentials copied."
                      : "Copy was blocked. Select the credentials above and copy them manually.",
                  );
                }}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                {copyFeedback === "Credentials copied."
                  ? "Copied!"
                  : "Copy credentials"}
              </button>
              {copyFeedback && (
                <p role="status" className="mt-2 text-sm text-green-800">
                  {copyFeedback}
                </p>
              )}
            </div>
            <button
              aria-label="Dismiss"
              onClick={() => {
                setCreatedCredentials(null);
                setCopyFeedback("");
              }}
            >
              <X className="h-5 w-5 text-green-700" />
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header pb-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Users className="h-5 w-5" /> Student directory
              </div>
              <p className="text-sm text-gray-500">
                Search, filter, review, and manage every learner record.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Export</span>
              {(
                [
                  ["csv", FileText, "CSV", "Download filtered students as CSV"],
                  ["excel", FileSpreadsheet, "Excel", "Download filtered students as Excel"],
                  ["pdf", Printer, "PDF", "Print or save filtered students as PDF"],
                ] as const
              ).map(([format, Icon, label, title]) => (
                <button
                  key={format}
                  type="button"
                  disabled={isExporting || !totalCount}
                  onClick={() => exportStudents(format)}
                  className="pager-button h-8"
                  title={title}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
          {isExporting && (
            <p className="mt-2 text-xs text-gray-500">
              Preparing the full filtered directory export…
            </p>
          )}
        </div>
        <div className="card-content space-y-4">
          <div className="filter-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div role="group" aria-label="Student status" className="segmented">
                {STUDENT_STATUS_TABS.map(([value, label]) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={filters.status === value}
                    onClick={() => updateFilters({ status: value })}
                    className="segmented-option"
                  >
                    {label}
                  </button>
                ))}
              </div>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                >
                  <X className="h-3.5 w-3.5" /> Clear {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"}
                </button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(16rem,2fr)_repeat(5,minmax(0,1fr))]">
              <label className="filter-field sm:col-span-2 lg:col-span-3 2xl:col-span-1">
                Search
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="input-compact pl-8"
                    placeholder="Name, ID, admission no., username or parent"
                  />
                </div>
              </label>
              <label className="filter-field">
                Class
                <select
                  className="input-compact"
                  value={filters.current_class}
                  onChange={(e) => updateFilters({ current_class: e.target.value })}
                >
                  <option value="">All classes</option>
                  {classesQuery.data?.results.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="filter-field">
                Gender
                <select
                  className="input-compact"
                  value={filters.gender}
                  onChange={(e) =>
                    updateFilters({
                      gender: e.target.value as StudentDirectoryFilters["gender"],
                    })
                  }
                >
                  <option value="">Any gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <label className="filter-field">
                Residence
                <select
                  className="input-compact"
                  value={filters.residence}
                  onChange={(e) =>
                    updateFilters({
                      residence: e.target.value as StudentDirectoryFilters["residence"],
                    })
                  }
                >
                  <option value="">Day and boarding</option>
                  <option value="day">Day scholars</option>
                  <option value="boarding">Boarders</option>
                </select>
              </label>
              <label className="filter-field">
                Transport
                <select
                  className="input-compact"
                  value={filters.uses_transport}
                  onChange={(e) =>
                    updateFilters({
                      uses_transport: e.target
                        .value as StudentDirectoryFilters["uses_transport"],
                    })
                  }
                >
                  <option value="">Any</option>
                  <option value="true">Uses school transport</option>
                  <option value="false">No school transport</option>
                </select>
              </label>
              <label className="filter-field">
                Sort by
                <select
                  className="input-compact"
                  value={filters.ordering}
                  onChange={(e) =>
                    updateFilters({
                      ordering: e.target.value as StudentDirectoryFilters["ordering"],
                    })
                  }
                >
                  {Object.entries(STUDENT_ORDERINGS).map(([value, [label]]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">Loading students...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No students found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeFilterCount
                  ? "Nothing matches these filters. Clear a filter or try another search."
                  : "Get started by adding your first student."}
              </p>
              {!activeFilterCount && canManageStudents && (
                <div className="mt-6">
                  <button
                    className="btn-primary"
                    onClick={() => setShowAddModal(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Student
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className={`sheet-wrap ${isFetching ? "opacity-60" : ""}`}>
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Name</th>
                      <th>Student ID</th>
                      <th>Adm. no.</th>
                      <th>Class</th>
                      <th>Gender</th>
                      <th className="text-right">Age</th>
                      <th>Residence</th>
                      <th>Transport</th>
                      <th>Parent / guardian</th>
                      <th>Parent phone</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => {
                      const guardian = primaryGuardian(student);
                      return (
                        <tr key={student.id}>
                          <td className="sheet-rownum">{firstVisible + index}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => setViewStudent(student)}
                              className="flex items-center gap-2 text-left hover:text-primary-700"
                              title={student.user.email || undefined}
                            >
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[9px] font-semibold text-primary-700">
                                {studentInitials(student)}
                              </span>
                              <span className="font-medium text-gray-900">
                                {student.user.first_name || "Unknown"}{" "}
                                {student.user.last_name || "Student"}
                              </span>
                            </button>
                          </td>
                          <td className="font-mono text-xs text-gray-700">
                            {student.student_id}
                          </td>
                          <td className="font-mono text-xs text-gray-600">
                            {student.admission_number || "—"}
                          </td>
                          <td className="text-gray-700">
                            {student.current_class_name || (
                              <span className="italic text-gray-400">Unassigned</span>
                            )}
                          </td>
                          <td className="capitalize text-gray-600">{student.gender}</td>
                          <td className="text-right tabular-nums text-gray-600">
                            {student.age}
                          </td>
                          <td className="capitalize text-gray-600">
                            {student.residence || "—"}
                          </td>
                          <td className="text-gray-600">
                            {student.uses_transport ? "Yes" : "—"}
                          </td>
                          <td
                            className="max-w-[12rem] truncate text-gray-700"
                            title={
                              guardian
                                ? `${guardian.name}${guardian.relationship ? ` (${guardian.relationship})` : ""}`
                                : undefined
                            }
                          >
                            {guardian?.name || (
                              <span className="italic text-gray-400">Not linked</span>
                            )}
                          </td>
                          <td className="tabular-nums text-gray-600">
                            {guardian?.phone || "—"}
                          </td>
                          <td>
                            <span className="inline-flex items-center gap-1 text-xs font-medium capitalize text-gray-700">
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${STUDENT_STATUS_DOTS[student.status] || "bg-gray-400"}`}
                              />
                              {student.status}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center justify-end gap-0.5">
                              <button
                                type="button"
                                onClick={() => setViewStudent(student)}
                                className="sheet-action text-primary-700"
                                title="View student"
                                aria-label={`View ${student.full_name}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              {canManageStudents && (
                                <button
                                  type="button"
                                  onClick={() => setEditStudent(student)}
                                  className="sheet-action text-gray-600"
                                  title="Edit placement"
                                  aria-label={`Edit ${student.full_name}`}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {canManageStudents && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    deleteStudentMutation.reset();
                                    setStudentPendingDeletion(student);
                                  }}
                                  className="sheet-action text-red-600"
                                  title="Delete student"
                                  aria-label={`Delete ${student.full_name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                <div className="flex flex-wrap items-center gap-4">
                  <span>
                    Showing{" "}
                    <strong className="font-semibold text-gray-900">
                      {firstVisible}–{lastVisible}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-gray-900">
                      {totalCount}
                    </strong>{" "}
                    students
                  </span>
                  <label className="inline-flex items-center gap-1.5">
                    Rows per page
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs"
                    >
                      {STUDENT_DIRECTORY_PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pager-button"
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={currentPage >= totalPages}
                    className="pager-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {studentPendingDeletion && (
        <DeleteStudentDialog
          key={studentPendingDeletion.id}
          student={studentPendingDeletion}
          isDeleting={deleteStudentMutation.isPending}
          error={deleteStudentMutation.error}
          onConfirm={() => deleteStudentMutation.mutate(studentPendingDeletion.id)}
          onCancel={() => setStudentPendingDeletion(null)}
          onChangeStatus={() => {
            setEditStudent(studentPendingDeletion);
            setStudentPendingDeletion(null);
          }}
        />
      )}

      {/* Add Student Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Add New Student
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register("first_name")}
                      className="input"
                      placeholder="Enter first name"
                    />
                    {errors.first_name && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.first_name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      {...register("last_name")}
                      className="input"
                      placeholder="Enter last name"
                    />
                    {errors.last_name && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.last_name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      {...register("username", {
                        onChange: () => setStudentUsernameEdited(true),
                      })}
                      className="input"
                      placeholder="e.g. smac"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {usernameHint(
                        studentUsername.base,
                        studentUsername.suggested,
                        studentUsernameEdited,
                      )}
                    </p>
                    {errors.username && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.username.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temporary portal password *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        {...register("password")}
                        className="input font-mono"
                      />
                      <button
                        type="button"
                        className="btn btn-outline shrink-0 px-3"
                        onClick={() =>
                          setValue("password", generatePassword(), {
                            shouldValidate: true,
                          })
                        }
                      >
                        New
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      readOnly
                      type="email"
                      tabIndex={-1}
                      value={studentEmail}
                      className="input bg-gray-50 text-gray-600"
                      placeholder={`username@${DEFAULT_SCHOOL_EMAIL_DOMAIN}`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Set from the username above; every account is reached at
                      its username@{DEFAULT_SCHOOL_EMAIL_DOMAIN}.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      {...register("phone_number")}
                      className="input"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender *
                    </label>
                    <select {...register("gender")} className="input">
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    {errors.gender && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.gender.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Student Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Student Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-blue-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Auto-Generated IDs
                          </h3>
                          <div className="mt-2 text-sm text-blue-700">
                            <p>
                              Student ID and Admission Number will be
                              automatically generated when you save the student.
                            </p>
                            <p className="mt-1">
                              <strong>Format:</strong> Student ID: STU{yearDigits}0001,
                              Admission: ADM{yearDigits}0001
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      {...register("date_of_birth")}
                      className="input"
                    />
                    {errors.date_of_birth && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.date_of_birth.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Admission Date *
                    </label>
                    <input
                      type="date"
                      {...register("admission_date")}
                      className="input"
                    />
                    {errors.admission_date && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.admission_date.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Class *
                    </label>
                    <select
                      {...register("current_class", { valueAsNumber: true })}
                      className="input"
                      disabled={classesQuery.isLoading}
                    >
                      <option value="">
                        {classesQuery.isLoading
                          ? "Loading classes…"
                          : "Select class"}
                      </option>
                      {classesQuery.data?.results.map((schoolClass) => (
                        <option key={schoolClass.id} value={schoolClass.id}>
                          {schoolClass.name} · {schoolClass.available_spots}{" "}
                          places available
                        </option>
                      ))}
                    </select>
                    {errors.current_class && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.current_class.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nationality *
                    </label>
                    <input
                      type="text"
                      {...register("nationality")}
                      className="input"
                      placeholder="Enter nationality"
                    />
                    {errors.nationality && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.nationality.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Blood Group
                    </label>
                    <select {...register("blood_group")} className="input">
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Medical Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Medical Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medical Conditions
                    </label>
                    <textarea
                      {...register("medical_conditions")}
                      className="input"
                      rows={3}
                      placeholder="Enter any medical conditions"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Allergies
                    </label>
                    <textarea
                      {...register("allergies")}
                      className="input"
                      rows={3}
                      placeholder="Enter any allergies"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medications
                    </label>
                    <textarea
                      {...register("medications")}
                      className="input"
                      rows={3}
                      placeholder="Enter current medications"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Doctor Name
                    </label>
                    <input
                      type="text"
                      {...register("doctor_name")}
                      className="input"
                      placeholder="Enter doctor's name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Doctor Phone
                    </label>
                    <input
                      type="tel"
                      {...register("doctor_phone")}
                      className="input"
                      placeholder="Enter doctor's phone"
                    />
                  </div>
                </div>
              </div>

              {/* Day or boarding */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Day or Boarding
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day or boarding *
                    </label>
                    <select {...register("residence")} className="input" defaultValue="">
                      <option value="" disabled>
                        Select day or boarding
                      </option>
                      <option value="day">Day student</option>
                      <option value="boarding">Boarding student</option>
                    </select>
                    {errors.residence && (
                      <p className="mt-1 text-sm text-red-600">{errors.residence.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Decides which fees this learner is billed.</p>
                  </div>
                </div>
              </div>

              {/* Transport Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Transport Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...register("uses_transport")}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Student uses school transport
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Transport fees are billed only to students who use school transport.
                  </p>

                  {usesTransport && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Transport Route
                        </label>
                        <input
                          type="text"
                          {...register("transport_route")}
                          className="input"
                          placeholder="Enter transport route"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Location
                        </label>
                        <input
                          type="text"
                          {...register("pickup_location")}
                          className="input"
                          placeholder="Enter pickup location"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Additional Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SurePay Number
                    </label>
                    <input
                      type="text"
                      {...register("surepay_number")}
                      className="input"
                      placeholder="e.g. SP-4417"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Fees paid through SurePay are matched to this learner by this number. Without it,
                      their payments have to be matched by hand.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Previous School
                    </label>
                    <input
                      type="text"
                      {...register("previous_school")}
                      className="input"
                      placeholder="Enter previous school name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      {...register("notes")}
                      className="input"
                      rows={3}
                      placeholder="Enter any additional notes"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={addParentDuringAdmission}
                    onChange={(event) => {
                      setAddParentDuringAdmission(event.target.checked);
                      if (event.target.checked && parentLinkMode === "new")
                        setValue("parent_password", generatePassword());
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span>
                    <span className="font-medium text-gray-900">
                      Link a parent or guardian
                    </span>
                    <span className="block text-sm text-gray-500">
                      Already have this family in the system? Link the existing
                      parent instead of creating a duplicate account.
                    </span>
                  </span>
                </label>
                {addParentDuringAdmission && (
                  <div className="mt-4 space-y-4 border-t pt-4">
                    <div className="flex gap-4 text-sm font-medium">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="parentLinkMode"
                          checked={parentLinkMode === "existing"}
                          onChange={() => setParentLinkMode("existing")}
                        />
                        Existing parent/guardian
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="parentLinkMode"
                          checked={parentLinkMode === "new"}
                          onChange={() => {
                            setParentLinkMode("new");
                            setValue("parent_password", generatePassword());
                          }}
                        />
                        New parent/guardian
                      </label>
                    </div>

                    {parentLinkMode === "existing" ? (
                      <div>
                        <input
                          type="text"
                          placeholder="Search by parent name or email…"
                          value={existingParentSearch}
                          onChange={(event) => setExistingParentSearch(event.target.value)}
                          className="input"
                        />
                        <select
                          required
                          value={selectedExistingParent?.id ?? ""}
                          onChange={(event) => {
                            const options = existingParentsQuery.data ?? [];
                            const match =
                              options.find((parent) => String(parent.id) === event.target.value) ?? null;
                            setSelectedExistingParent(match);
                          }}
                          className="input mt-2"
                        >
                          <option value="">
                            {existingParentSearchDebounced.length < 2
                              ? "Type to search…"
                              : existingParentsQuery.isFetching
                                ? "Searching…"
                                : "Select a parent…"}
                          </option>
                          {(selectedExistingParent &&
                          !existingParentsQuery.data?.some((p) => p.id === selectedExistingParent.id)
                            ? [selectedExistingParent, ...(existingParentsQuery.data ?? [])]
                            : (existingParentsQuery.data ?? [])
                          ).map((parent) => (
                            <option key={parent.id} value={parent.id}>
                              {parent.full_name} ({parent.user.email || "no email"})
                            </option>
                          ))}
                        </select>
                        {!!selectedExistingParent?.children_data?.length && (
                          <p className="mt-1 text-xs text-gray-500">
                            Already linked to {selectedExistingParent.children_data.length} other
                            child{selectedExistingParent.children_data.length === 1 ? "" : "ren"}.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        <label className="text-sm font-medium">
                          First name *
                          <input
                            required
                            {...register("parent_first_name")}
                            className="input mt-1"
                          />
                        </label>
                        <label className="text-sm font-medium">
                          Last name *
                          <input
                            required
                            {...register("parent_last_name")}
                            className="input mt-1"
                          />
                        </label>
                        <label className="text-sm font-medium">
                          Username *
                          <input
                            required
                            {...register("parent_username", {
                              onChange: () => setParentUsernameEdited(true),
                            })}
                            className="input mt-1"
                            placeholder="e.g. mruth"
                          />
                          <span className="mt-1 block text-xs font-normal text-gray-500">
                            {usernameHint(
                              parentUsername.base,
                              parentUsername.suggested,
                              parentUsernameEdited,
                            )}
                          </span>
                        </label>
                        <label className="text-sm font-medium">
                          Temporary password *
                          <div className="mt-1 flex gap-2">
                            <input
                              required
                              minLength={8}
                              {...register("parent_password")}
                              className="input font-mono"
                            />
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() =>
                                setValue("parent_password", generatePassword())
                              }
                            >
                              New
                            </button>
                          </div>
                        </label>
                        <label className="text-sm font-medium">
                          Email
                          <input
                            readOnly
                            type="email"
                            tabIndex={-1}
                            value={parentEmail}
                            className="input mt-1 bg-gray-50 text-gray-600"
                            placeholder={`username@${DEFAULT_SCHOOL_EMAIL_DOMAIN}`}
                          />
                          <span className="mt-1 block text-xs font-normal text-gray-500">
                            Set from the parent's username.
                          </span>
                        </label>
                        <label className="text-sm font-medium">
                          Phone
                          <input
                            {...register("parent_phone_number")}
                            className="input mt-1"
                          />
                        </label>
                        <label className="text-sm font-medium">
                          Relationship *
                          <select
                            required
                            {...register("parent_relationship")}
                            defaultValue="guardian"
                            className="input mt-1"
                          >
                            <option value="father">Father</option>
                            <option value="mother">Mother</option>
                            <option value="guardian">Guardian</option>
                            <option value="grandfather">Grandfather</option>
                            <option value="grandmother">Grandmother</option>
                            <option value="uncle">Uncle</option>
                            <option value="aunt">Aunt</option>
                            <option value="other">Other</option>
                          </select>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {createStudentMutation.isError && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  {submissionError(createStudentMutation.error)}
                  {possibleDuplicate(createStudentMutation.error) && (
                    <label className="mt-3 flex items-start gap-2 text-gray-800">
                      <input type="checkbox" className="mt-0.5" {...register("confirm_not_duplicate")} />
                      <span>This is a different child with the same name and date of birth. Admit them.</span>
                    </label>
                  )}
                </div>
              )}

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createStudentMutation.isPending}
                  className="btn-primary"
                >
                  {createStudentMutation.isPending
                    ? "Adding..."
                    : "Add Student"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {viewStudent && (
        <StudentProfileModal
          student={viewStudent}
          canEdit={canManageStudents}
          canViewFinancials={canViewFinancials}
          onEdit={() => {
            setEditStudent(viewStudent);
            setReturnToProfile(true);
            setViewStudent(null);
          }}
          onClose={() => setViewStudent(null)}
        />
      )}
      {editStudent && (
        <StudentEditModal
          student={editStudent}
          classes={classesQuery.data?.results || []}
          onClose={(saved) => {
            // Opened from the profile: go back to it, showing any changes.
            if (returnToProfile) setViewStudent(saved || editStudent);
            setReturnToProfile(false);
            setEditStudent(null);
          }}
        />
      )}
    </div>
  );
}

export default function Students() {
  return (
    <Routes>
      <Route index element={<StudentsList />} />
      <Route path="*" element={<StudentsList />} />
    </Routes>
  );
}
